import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import {
  Wrench, CheckCircle, Clock, XCircle, Phone, MapPin,
  ArrowLeft, Loader2, Search, Calendar, User as UserIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: '#eab308', bg: 'bg-yellow-50 border-yellow-200' },
  confirmed: { label: 'Confirmed', color: '#2563eb', bg: 'bg-blue-50 border-blue-200' },
  assigned: { label: 'Technician Assigned', color: '#7c3aed', bg: 'bg-purple-50 border-purple-200' },
  in_progress: { label: 'In Progress', color: '#ea580c', bg: 'bg-orange-50 border-orange-200' },
  completed: { label: 'Completed', color: '#16a34a', bg: 'bg-green-50 border-green-200' },
  cancelled: { label: 'Cancelled', color: '#dc2626', bg: 'bg-red-50 border-red-200' }
};

function BookingTrackingPage() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchId, setSearchId] = useState('');

  const fetchBooking = async (bookingId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/track/booking/${bookingId}`);
      if (res.ok) {
        setBooking(await res.json());
      } else {
        setError('Booking not found. Please check your booking ID.');
        setBooking(null);
      }
    } catch {
      setError('Unable to fetch booking details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchBooking(id);
    else setLoading(false);
  }, [id]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchId.trim()) fetchBooking(searchId.trim());
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const statusCfg = booking ? (STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending) : STATUS_CONFIG.pending;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="booking-tracking-page">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600 mb-6">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <h1 className="text-2xl font-bold text-gray-900 mb-6">Track Your Booking</h1>

        {!id && (
          <form onSubmit={handleSearch} className="flex gap-2 mb-8" data-testid="booking-search-form">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                placeholder="Enter Booking ID"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="booking-search-input"
              />
            </div>
            <button type="submit" className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium" data-testid="booking-search-btn">
              Track
            </button>
          </form>
        )}

        {loading && <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>}

        {error && (
          <div className="text-center py-16" data-testid="booking-not-found">
            <Wrench className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">{error}</p>
          </div>
        )}

        {booking && !loading && (
          <div className="space-y-6" data-testid="booking-tracking-details">
            {/* Header */}
            <div className={`border rounded-2xl p-6 ${statusCfg.bg}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-500">Booking ID</p>
                  <p className="text-lg font-bold text-gray-900" data-testid="booking-id">{booking.booking_id}</p>
                  <p className="text-sm text-gray-600 mt-1 capitalize">{booking.service_type?.replace(/-/g, ' ')}</p>
                </div>
                <span className="px-4 py-1.5 rounded-full text-sm font-bold text-white" style={{ backgroundColor: statusCfg.color }} data-testid="booking-status-badge">
                  {statusCfg.label}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-2">Booked on {formatDate(booking.created_at)}</p>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-2xl border p-6" data-testid="booking-timeline">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Service Timeline</h2>
              <div className="relative">
                {booking.timeline.map((step, idx) => {
                  const isLast = idx === booking.timeline.length - 1;
                  const isCancelled = step.step === 'cancelled';
                  return (
                    <div key={step.step} className="flex gap-4 relative" data-testid={`timeline-step-${step.step}`}>
                      {!isLast && (
                        <div className={`absolute left-[15px] top-8 w-0.5 h-full ${step.completed ? 'bg-blue-200' : 'bg-gray-200'}`} />
                      )}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 ${
                        isCancelled ? 'bg-red-100' : step.completed ? 'bg-blue-600' : 'bg-gray-200'
                      }`}>
                        {isCancelled ? <XCircle size={16} className="text-red-600" /> :
                         step.completed ? <CheckCircle size={16} className="text-white" /> :
                         <Clock size={16} className="text-gray-400" />}
                      </div>
                      <div className={`pb-6 ${!step.completed && !isCancelled ? 'opacity-40' : ''}`}>
                        <p className={`font-semibold ${isCancelled ? 'text-red-600' : 'text-gray-900'}`}>{step.label}</p>
                        <p className="text-sm text-gray-500">{step.description}</p>
                        {step.timestamp && <p className="text-xs text-gray-400 mt-1">{formatDate(step.timestamp)}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Service & Contact Details */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl border p-6">
                <div className="flex items-center gap-2 mb-3 text-gray-700"><Wrench size={18} /><h3 className="font-semibold">Service Details</h3></div>
                <p className="text-gray-900 font-medium capitalize">{booking.service_type?.replace(/-/g, ' ')}</p>
                {booking.ro_model && <p className="text-gray-500 text-sm mt-1">Model: {booking.ro_model}</p>}
                {booking.preferred_date && (
                  <p className="text-gray-500 text-sm mt-2 flex items-center gap-1">
                    <Calendar size={14} /> {booking.preferred_date} {booking.preferred_time ? `at ${booking.preferred_time}` : ''}
                  </p>
                )}
                {booking.assigned_technician && (
                  <p className="text-purple-600 text-sm mt-2 flex items-center gap-1 font-medium">
                    <UserIcon size={14} /> Technician: {booking.assigned_technician}
                  </p>
                )}
              </div>
              <div className="bg-white rounded-2xl border p-6">
                <div className="flex items-center gap-2 mb-3 text-gray-700"><MapPin size={18} /><h3 className="font-semibold">Contact Info</h3></div>
                <p className="text-gray-900 font-medium">{booking.name}</p>
                {booking.address && <p className="text-gray-600 text-sm mt-1">{booking.address}</p>}
                {booking.city && <p className="text-gray-600 text-sm">{booking.city} - {booking.pincode}</p>}
                {booking.phone && <p className="text-gray-500 text-sm mt-1 flex items-center gap-1"><Phone size={14} />{booking.phone}</p>}
              </div>
            </div>

            {booking.issue && (
              <div className="bg-white rounded-2xl border p-6">
                <h3 className="font-semibold text-gray-700 mb-2">Issue Description</h3>
                <p className="text-gray-600">{booking.issue}</p>
              </div>
            )}

            {/* Support */}
            <div className="text-center py-4">
              <p className="text-gray-500 text-sm mb-2">Need help with this booking?</p>
              <div className="flex justify-center gap-4">
                <a href="tel:+919887068888" className="text-blue-600 text-sm font-medium hover:underline">Call +91 9887068888</a>
                <a href="https://wa.me/919887068888" target="_blank" rel="noopener noreferrer" className="text-green-600 text-sm font-medium hover:underline">WhatsApp</a>
              </div>
            </div>
          </div>
        )}

        {!id && !loading && !booking && !error && (
          <div className="text-center py-16">
            <Wrench className="w-20 h-20 text-gray-200 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Track Your Booking</h2>
            <p className="text-gray-500">Enter your booking ID above to see the service status</p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default BookingTrackingPage;
